/**
 * client/src/auth/RequireAuth.tsx — the auth guard, and what it shows before it knows.
 *
 * LIFTED OUT OF `App.tsx` ON 2026-09-12, FOR A TESTABILITY REASON WORTH RECORDING. The guard's
 * behaviour while the session check is in flight is the defect this module exists to pin, and a
 * test of it that imports `App.tsx` drags in the landing page, the hero canvas and the whole CMA
 * wizard — the run did not finish inside two minutes. The guard depends on a router hook, one API
 * call and one error type; nothing else. Split out, its test costs a second.
 */
import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { getMe } from "../cma/api";
import { ApiError } from "../lib/auth";

/** How long the session check may run before the screen admits it is taking a while. */
const SLOW_SESSION_CHECK_MS = 2500;

/**
 * What a guarded route shows while it does not yet know who you are.
 *
 * IT USED TO SHOW NOTHING, AND "NOTHING" WAS THE BUG (found 2026-09-12, on the live site).
 * `RequireAuth` returned `null` while the session check was in flight, so opening a brief from
 * `/briefs` painted an entirely empty document — no wordmark, no frame, no text, nothing to read
 * and nothing to wait on. Measured on valubrief.ai: a cold load of `/cma/<id>` sat blank long
 * enough to look permanent, and the owner's report of it was "clicking my brief does nothing."
 *
 * THE ORIGINAL REASONING WAS RIGHT AND IS KEPT. Flashing an upload form at somebody who is about
 * to be redirected to `/login` reads as a bug, and starting an upload that is about to be thrown
 * away is worse. So the guarded screen still does not render early. What changed is that the
 * ALTERNATIVE to it is no longer a void: this says the app is doing something, commits to nothing
 * the visitor could act on, and cannot be mistaken for content that is about to be taken away.
 *
 * AND IT SAYS SO WHEN IT IS SLOW. A check that has run past `SLOW_SESSION_CHECK_MS` adds a second
 * line, because the difference between "loading" and "broken" is the only thing the person
 * actually wants to know, and a screen that never changes cannot tell them.
 */
function SessionCheck(): ReactElement {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), SLOW_SESSION_CHECK_MS);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div className="vb-session-check" role="status" aria-live="polite">
      <p className="vb-session-check-word">ValuBrief</p>
      <p className="vb-session-check-line">Opening…</p>
      {/* Calm, sentence case, no alarm styling — it is a delay, not a failure. */}
      {slow && (
        <p className="vb-session-check-slow">
          Still checking your session. This is taking longer than usual.
        </p>
      )}
    </div>
  );
}

/**
 * UI-009 — the auth guard.
 *
 * Sends the user to `/login?next=<where they were going>` so signing in returns them to the job
 * they were opening, rather than to a generic landing screen. While the session check is in flight
 * the guarded screen is not rendered — see `SessionCheck` above for what is shown instead, and for
 * why "nothing" was the wrong answer to that question.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<"checking" | "in" | "out">("checking");

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((r) => {
        if (!cancelled) setState(r.user ? "in" : "out");
      })
      .catch((e) => {
        if (cancelled) return;
        // Only a real 401 means "not signed in". A network blip or a 500 must NOT throw a working
        // session out of a job mid-flow — let the screen's own calls surface that failure instead.
        setState(e instanceof ApiError && e.status === 401 ? "out" : "in");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "checking") return <SessionCheck />;
  if (state === "out") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
